import {AsyncInlineError, tracer} from '@s-ui/decorators'
import {UseCase} from '@s-ui/domain'
import jsCookie from '@s-ui/js/lib/cookie'

import MotorFetcherFactory from '../../fetcher/Fetchers/factory.js'
import {UserErrorsFactory} from '../../user/Errors/factory.js'
import UserServicesFactory from '../../user/Services/factory.js'
import {CookieUserDiscardedAdsRepository} from '../Repositories/CookieUserDiscardedAdsRepository.js'
import {HttpUserDiscardedAdsRepository} from '../Repositories/HttpUserDiscardedAdsRepository.js'

class SyncCookieDiscardedAdsUseCase extends UseCase {
  #config
  #getCurrentUserService
  #cookieRepository
  #httpRepository
  #userNotLoggedInError

  static create({config}) {
    return new SyncCookieDiscardedAdsUseCase({
      config,
      getCurrentUserService: UserServicesFactory.getCurrentUserService({config}),
      cookieRepository: CookieUserDiscardedAdsRepository.create({config}),
      httpRepository: HttpUserDiscardedAdsRepository.create({
        config,
        authenticationFetcher: MotorFetcherFactory.httpAuthenticationFetcher({config})
      }),
      userNotLoggedInError: UserErrorsFactory.userNotLoggedInUserError
    })
  }

  constructor({config, getCurrentUserService, cookieRepository, httpRepository, userNotLoggedInError}) {
    super()
    this.#config = config
    this.#getCurrentUserService = getCurrentUserService
    this.#cookieRepository = cookieRepository
    this.#httpRepository = httpRepository
    this.#userNotLoggedInError = userNotLoggedInError
  }

  @tracer({metric: 'SyncCookieDiscardedAdsUseCase#execute'})
  @AsyncInlineError()
  async execute() {
    const userEntity = await this.#getCurrentUserService.execute()

    if (!userEntity)
      return Promise.reject(
        this.#userNotLoggedInError({message: '[SyncCookieDiscardedAdsUseCase#execute] User is not logged in'})
      )

    const userId = userEntity.getId()
    const cookieValueObject = await this.#cookieRepository.getDiscardedAds({userId})
    const {items} = cookieValueObject.toJSON()

    if (!items.length) return true

    const cookiePrefix = this.#config.get('cookieLayer').COOKIE_PREFIX_DISCARDED_ADS
    const cookieName = `${cookiePrefix}${userId}`

    for (const adId of items) {
      try {
        await this.#httpRepository.addDiscardedAd({adId, userId})

        const currentCookieRaw = jsCookie.get(cookieName)
        if (currentCookieRaw) {
          const remaining = JSON.parse(currentCookieRaw).filter(id => id !== adId)
          if (remaining.length === 0) {
            jsCookie.remove(cookieName)
          } else {
            const cookieExpiration = this.#config.get('cookieLayer').COOKIE_DISCARDED_ADS_EXPIRATION
            jsCookie.set(cookieName, JSON.stringify(remaining), {expires: cookieExpiration})
          }
        }
      } catch {
        // Keep this ID in the cookie — will retry on next load
      }
    }

    return true
  }
}

export const factory = SyncCookieDiscardedAdsUseCase.create
